import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export default function HomeSolution() {
  const { lang } = useLanguage();

  const features = lang === 'en'
    ? [
        { icon: 'ri-test-tube-line', title: 'Ultra-Low Dosage', desc: 'Only 500–1000 ppm required. Minimal addition delivers significant improvement in recycled material properties while reducing material costs.' },
        { icon: 'ri-settings-3-line', title: 'Process-Friendly', desc: 'Compatible with existing compounding, injection, and extrusion lines. No major equipment overhaul needed for rapid integration.' },
        { icon: 'ri-stack-line', title: 'Multi-Resin Compatible', desc: 'Works with rABS, rPET, rPE, rPP, and composite plastics — covering the most common waste plastic types.' },
        { icon: 'ri-earth-line', title: 'Low-Carbon Value', desc: 'Helps companies build ESG and carbon reduction outcomes, quantify circular economy benefits, and support sustainable brand transformation.' },
      ]
    : lang === 'ja'
    ? [
        { icon: 'ri-test-tube-line', title: '超低添加量', desc: '500〜1000 ppmの添加で再生材料の物性を大幅に改善。材料コストを抑えながら高い効果を発揮します。' },
        { icon: 'ri-settings-3-line', title: 'プロセスフレンドリー', desc: '既存の造粒・射出・押出ラインに対応。大規模な設備改造なしに迅速に導入できます。' },
        { icon: 'ri-stack-line', title: '多樹脂対応', desc: 'rABS・rPET・rPE・rPP・複合プラスチックに対応し、主要な廃プラスチック材質をカバーします。' },
        { icon: 'ri-earth-line', title: '低炭素価値', desc: 'ESGと炭素削減の成果構築を支援し、サーキュラーエコノミーの効果を定量化してブランドの持続可能な転換をサポートします。' },
      ]
    : [
        { icon: 'ri-test-tube-line', title: '低添加量', desc: '500–1000 ppm 即可導入，極低用量即可顯著提升再生料物性，降低材料成本。' },
        { icon: 'ri-settings-3-line', title: '製程友善', desc: '可搭配既有造粒、射出、押出產線，無需大幅改造設備，快速整合現有製程。' },
        { icon: 'ri-stack-line', title: '多材質適用', desc: '適用 rABS、rPET、rPE、rPP 與複合塑料，覆蓋主流廢棄塑膠材質類型。' },
        { icon: 'ri-earth-line', title: '低碳價值', desc: '協助企業建立 ESG 與減碳成果，量化循環經濟效益，支持品牌永續轉型。' },
      ];

  const sectionLabel = lang === 'en' ? 'Core Solution' : lang === 'ja' ? 'コアソリューション' : '核心解決方案';
  const heading = lang === 'en'
    ? <>PQernel® Makes Recycled Plastics<br />More Stable, Durable &amp; Scalable</>
    : lang === 'ja'
    ? <>PQernel®が再生プラスチックを<br />より安定・耐久・量産可能に</>
    : <>PQernel® 讓再生塑料<br />更穩定、更耐用、更可量產</>;

  const body = lang === 'en'
    ? 'PQernel® is a proprietary nano-scale polymer chain extender developed by PolyQ. It can be directly added to shredded or compounded waste plastics, significantly improving mechanical strength, processing stability, and material compatibility — without major equipment changes.'
    : lang === 'ja'
    ? 'PQernel®は聚量応用材料が独自開発したナノスケール高分子鎖延長補強剤です。廃プラスチックの粉砕料や造粒料に直接添加でき、既存設備を大幅に変更することなく、機械的強度・加工安定性・材料相容性を大幅に向上させます。'
    : 'PQernel® 是聚量自主開發的奈米級高分子擴鏈補強劑，可直接加入廢棄塑膠粉碎料或造粒料，在不大幅改變既有設備的條件下，提升再生塑料的機械強度、加工穩定性與材料相容性。';

  const ctaLabel = lang === 'en' ? 'Learn More About PQernel® Technology' : lang === 'ja' ? 'PQernel®技術の詳細を見る' : '深入了解 PQernel® 技術';
  const badgeTitle = lang === 'en' ? 'PQernel® Core Technology' : lang === 'ja' ? 'PQernel® コア技術' : 'PQernel® 核心技術';
  const badgeSub = lang === 'en' ? 'Nano-scale Polymer Chain Extender · Proprietary R&D' : lang === 'ja' ? 'ナノスケール高分子鎖延長剤・独自開発' : '奈米級高分子擴鏈補強劑 · 自主研發';

  return (
    <section className="section-padding bg-white overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">{sectionLabel}</p>
            <h2 className="font-bold text-neutral-dark mb-5" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}>{heading}</h2>
            <p className="text-neutral-mid mb-8" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>{body}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {features.map((f) => (
                <div key={f.title} className="flex items-start gap-4 p-4 rounded-xl bg-[#F5F8FB] border border-neutral-border">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0">
                    <i className={`${f.icon} text-brand-green text-xl`}></i>
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-dark text-[1.0625rem] mb-1">{f.title}</p>
                    <p className="text-neutral-mid text-[1rem] leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/technology" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-green text-white text-[1.0625rem] font-semibold hover:bg-brand-green-dark transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-flask-line"></i>{ctaLabel}
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -top-10 -right-10 w-72 h-72 opacity-40 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,184,148,0.20) 0%, transparent 70%)' }} aria-hidden="true" />
            <div className="absolute -bottom-10 -left-10 w-56 h-56 opacity-30 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(2,115,194,0.18) 0%, transparent 70%)' }} aria-hidden="true" />
            <div className="relative rounded-2xl overflow-hidden w-full" style={{ height: '460px' }}>
              <img src="https://readdy.ai/api/search-image?query=nano%20polymer%20material%20science%20laboratory%20with%20recycled%20plastic%20granules%20pellets%20in%20petri%20dish%20molecular%20chain%20structure%20visualization%20green%20technology%20clean%20white%20lab%20environment%20professional%20industrial%20photography%20macro%20detail%20shot%20with%20green%20and%20blue%20accent%20lighting&width=640&height=460&seq=solution-img-v1&orientation=portrait" alt={badgeTitle} className="w-full h-full object-cover object-top" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-white/50 rounded-xl px-5 py-4 border border-neutral-200/60" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brand-green-light flex-shrink-0">
                      <i className="ri-award-line text-brand-green text-xl"></i>
                    </div>
                    <div>
                      <p className="font-bold text-neutral-dark text-[1.0625rem]">{badgeTitle}</p>
                      <p className="text-neutral-mid text-[0.9375rem]">{badgeSub}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
